import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import {
  selectRecipes, parseSidesTitle, extractSideIngredients, isStockItem,
  parseDatesFromMessage, generateICS, formatDuration, dedupeShoppingList,
  loadPickHistory, savePickHistory, recentlyPickedIds, formatPickHistoryForPrompt,
  parseKeepCommand, pickReplacements,
  buildShoppingChecklist, checklistToText,
  parseTimeLimit, filterByTimeLimit, parseIngredientTerms, isCalendarDownloadRequest,
  formatTotalTime, summarizeIngredients, lastEatenLabel,
} from './mealUtils';

const escapeHtml = (s) => String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');

// Renders one meal card into an array of HTML/text lines.
// Called both by runMealPlan (first display) and the swap handler (single card rebuild).
const buildMealCard = (mealData, imageUrl) => {
  const lines = [];
  lines.push(mealData.mealNum);

  if (mealData.recipe) {
    const sidesIngredient = parseSidesTitle(mealData.sides);
    const titleSuffix = sidesIngredient ? ` + ${sidesIngredient}` : '';

    if (imageUrl) {
      lines.push(
        `<div style="display:flex;align-items:flex-start;gap:12px;margin:4px 0;">` +
        `<img src="${imageUrl}" alt="${escapeHtml(mealData.recipe.name)}" style="width:120px;height:120px;object-fit:cover;border-radius:8px;flex-shrink:0;" />` +
        `<div><strong>${escapeHtml(mealData.recipe.name)}${escapeHtml(titleSuffix)}</strong>` +
        `<br/>📖 <a href="${mealData.recipe.url}" target="_blank" style="color:#2E5FF3;text-decoration:underline;">View Recipe</a></div></div>`
      );
    } else {
      lines.push(`- **${mealData.recipe.name}${titleSuffix}**`);
      lines.push(`📖 <a href="${mealData.recipe.url}" target="_blank" style="color: #2E5FF3; text-decoration: underline;">View Recipe</a>`);
    }

    lines.push(`- Cooking time: ${formatTotalTime(mealData.recipe.totalTime)}`);
    lines.push('- Shopping list:');

    const shoppingList = [...(mealData.recipe.ingredients || [])];
    if (mealData.sides) shoppingList.push(...extractSideIngredients(mealData.sides));
    dedupeShoppingList(shoppingList).filter(item => !isStockItem(item))
      .forEach(item => lines.push(`  • ${item}`));
  } else {
    lines.push(`⚠️ Recipe ID "${mealData.recipeId}" not found in database`);
    if (mealData.sides) lines.push(`- Add to complete meal: ${mealData.sides}`);
  }

  return lines;
};

// Calls the AI for a single recipe and returns the "Add to complete meal" string.
// otherMeals provides context so the AI can vary proteins across the full plan.
const getAISidesForRecipe = async (recipe, otherMeals, signal, pickHistory = []) => {
  const otherContext = otherMeals
    .filter(m => m.recipe)
    .map(m => `- ${m.recipe.name}${m.sides && !/^nothing needed/i.test(m.sides) ? ` (added: ${m.sides})` : ''}`)
    .join('\n');

  const historyContext = formatPickHistoryForPrompt(pickHistory);

  const systemPrompt = `You are a meal planning assistant. Analyze this ONE recipe and suggest what sides to add to make a complete dinner.

Recipe: ${recipe.name}
Ingredients: ${recipe.ingredients.join(', ')}${recipe.servesWith ? `\nThe recipe says to serve it with: ${recipe.servesWith} — use that as the carb, do not substitute.` : ''}

${otherContext ? `The other meals in this plan are FIXED and must not change:\n${otherContext}\n\nVary proteins — do NOT repeat the same protein type as those meals.` : ''}
${historyContext ? `\n${historyContext}\n` : ''}

A complete dinner needs all three:
1. PROTEIN: meat, fish, tofu, tempeh, beans, chickpeas, eggs, or cheese as a main ingredient
2. VEGETABLES: a real vegetable (not just onions/garlic used as aromatics)
3. CARBS: rice, pasta, bread, potatoes, quinoa, etc.

Only add what is MISSING. Onions/garlic as aromatics do NOT count as vegetables. Cheese and eggs DO count as protein.

If the recipe is a component rather than a centerpiece — a sauce, dressing, vinaigrette, pesto or dip — do not add sides to it as though it were the main dish. Name the dish it belongs to and build the meal around it: a dressing gets the salad it dresses plus a protein, a pasta sauce gets the pasta. A dish that merely has a sauce in its name is a full dinner, not a component.

Reply with exactly one line:
Add to complete meal: [ingredient names only, e.g. "baked tofu + brown rice", or "Nothing needed"]`;

  const response = await fetch('/api/chat', {
    signal,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      messages: [{ role: 'user', content: 'Analyze this recipe and suggest sides.' }],
      systemPrompt
    })
  });

  if (!response.ok) throw new Error(`API error ${response.status}`);

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let fullResponse = '';
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      try {
        const data = JSON.parse(line.substring(6));
        if (data.done) fullResponse = data.content[0].text;
        else if (data.error) throw new Error(data.error);
      } catch (e) {}
    }
  }

  const match = fullResponse.match(/Add to complete meal:\s*(.+)/i);
  return match ? match[1].trim() : null;
};

// The prompt shown after every plan. Kept in one place so the chat commands
// and the plan panel never describe different options.
const nextStepsMessage = [
  'What would you like to do?',
  '• **Keep some** — say "keep 2, redo the others" (or tick the locks below)',
  '• **Swap** — say "swap 1" (or 2 or 3) to re-roll one',
  '• **Calendar** — give me 3 dates (e.g. "feb 5, 6, 7") and I\'ll make an ICS file',
  '• **New** — say "new" to pick 3 fresh recipes',
].join('\n');

const MealPlannerChat = () => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hi! I\'m Mama\'s Menu Maker. I\'m ready to plan your dinners! Got any spare ingredients you want to use up as sides? List them, or just type "go" to get started!'
    }
  ]);
const [input, setInput] = useState('');
const [loading, setLoading] = useState(false);
const [loadingStatus, setLoadingStatus] = useState(''); // ADD THIS LINE
const [recipes, setRecipes] = useState([]);
const [pendingCalendar, setPendingCalendar] = useState(null); // ADD THIS LINE
const [, setLastAssistantMessage] = useState(null);
const [currentMealPlan, setCurrentMealPlan] = useState(null); // persists { selectedRecipes, meals }
const [originalUserTerms, setOriginalUserTerms] = useState([]);
const [seenRecipeIds, setSeenRecipeIds] = useState([]);
// Which weeks' meals have already been eaten, newest first. Read once on
// mount and kept in step with localStorage on every save.
const [pickHistory, setPickHistory] = useState([]);
// Meal indexes the user has locked. Cleared whenever a fresh plan replaces
// the whole set, since the numbers would point at different meals.
const [lockedIndexes, setLockedIndexes] = useState([]);
// Keys of the checklist items the user still has to buy.
const [checkedItems, setCheckedItems] = useState([]);
const messagesEndRef = useRef(null);
const abortControllerRef = useRef(null);

// The history is read through this rather than window.localStorage directly
// so a browser that blocks site data just gives an empty history.
const storage = typeof window !== 'undefined' ? window.localStorage : null;

  // Load recipes on mount
  useEffect(() => {
    fetch('/recipes.json')
      .then(res => res.json())
      .then(data => {
        // Add unique IDs to recipes if they don't have them
        const recipesWithIds = data
          .filter(r => r && typeof r.name === 'string' && r.name.trim())
          .map(recipe => ({
            id: recipe.id || recipe.name.toLowerCase()
              .replace(/[^a-z0-9\s]/g, '')
              .replace(/\s+/g, '_')
              .trim(),
            name: recipe.name,
            ingredients: Array.isArray(recipe.ingredients) ? recipe.ingredients : [],
            // null means the recipe's doc never stated a time. Keep it null
            // rather than guessing, so the UI can say "unknown".
            cookTime: typeof recipe.cookTime === 'number' ? recipe.cookTime : null,
            totalTime: typeof recipe.totalTime === 'number' ? recipe.totalTime : null,
            // What the recipe says to serve it with, when it says.
            servesWith: typeof recipe.servesWith === 'string' ? recipe.servesWith : null,
            url: recipe.url || '',
          }));
        setRecipes(recipesWithIds);
        console.log('✅ Loaded', recipesWithIds.length, 'recipes with IDs:', recipesWithIds.slice(0, 3).map(r => `${r.name} -> ${r.id}`));
      })
      .catch(err => console.error('Error loading recipes:', err));
  }, []);

  // Load the saved pick history on mount
  useEffect(() => {
    setPickHistory(loadPickHistory(storage));
    // storage is derived from window and never changes for the life of the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Record a week's picks. Saving the same 3 recipes twice (accepting the
  // plan and then downloading its calendar) collapses into one entry.
  const recordPicks = (selectedRecipes) => {
    setPickHistory(savePickHistory(selectedRecipes, storage));
  };

  const alreadySaved = (selectedRecipes) => {
    const signature = selectedRecipes.map(r => r.id).sort().join('|');
    return pickHistory.some(entry =>
      entry.recipes.map(r => r.id).sort().join('|') === signature
    );
  };

const downloadICSFile = (icsContent) => {
  const blob = new Blob([icsContent], { type: 'text/calendar' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'meals.ics';
  a.click();
  URL.revokeObjectURL(url);

  // Putting the meals on the calendar is the clearest sign they were
  // accepted, so that is the moment they join the history.
  if (currentMealPlan) recordPicks(currentMealPlan.selectedRecipes);
};


const generateCalendarFromMessage = (userMessage) => {
  const dates = parseDatesFromMessage(userMessage);

  if (dates.length === 0 || !currentMealPlan) {
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: !currentMealPlan
        ? `No meal plan yet! Hit Go to get one first.`
        : `I need 3 dates to make a calendar. Try something like "Jan 10, 12, 14".`
    }]);
    return;
  }

  // Build meals array with full stock-filtered shopping list for ICS
  const meals = currentMealPlan.selectedRecipes.map((recipe, i) => {
    const sides = currentMealPlan.meals[i]?.sides || '';
    const shoppingList = [
      ...(recipe.ingredients || []),
      ...extractSideIngredients(sides)
    ].filter(item => !isStockItem(item));
    return {
      name: recipe.name,
      addedComponents: parseSidesTitle(sides),
      url: recipe.url,
      cookTime: recipe.cookTime,
      totalTime: recipe.totalTime,
      shoppingList: dedupeShoppingList(shoppingList),
    };
  });

  const icsContent = generateICS(meals, dates, recipes);
  setPendingCalendar({ meals, dates, icsContent });

  setMessages(prev => [...prev, {
    role: 'assistant',
    content: `Got it! Hit the **Download ICS File** button below, or just press Enter, and I'll save it for you.`
  }]);
};





// Re-roll the meals at `rerollIndexes`, leaving every other meal exactly as
// it is: same recipe, same sides, same photo. The kept meals are passed to
// the AI as context for each replacement, so it can vary proteins against
// them instead of dropping or rewriting them.
const rerollMeals = async (rerollIndexes) => {
  if (!currentMealPlan || rerollIndexes.length === 0) return;

  const n = currentMealPlan.selectedRecipes.length;
  const rerolling = [...rerollIndexes].sort((a, b) => a - b);
  const keptIndexes = Array.from({ length: n }, (_, i) => i).filter(i => !rerolling.includes(i));
  const keptRecipes = keptIndexes.map(i => currentMealPlan.selectedRecipes[i]);

  const label = rerolling.length === 1
    ? `meal ${rerolling[0] + 1}`
    : `meals ${rerolling.map(i => i + 1).join(' and ')}`;

  setLoading(true);
  setLoadingStatus(`🔄 Picking a new recipe for ${label}...`);

  try {
    // Only steer the pick toward ingredient terms the kept meals do not
    // already cover. If "chuck roast" is in the meal we are keeping, the
    // replacement does not need to be another chuck roast recipe.
    const unrepresentedTerms = originalUserTerms.filter(term =>
      !keptRecipes.some(r =>
        r.ingredients.some(ing => ing.toLowerCase().includes(term.toLowerCase()))
      )
    );

    // Avoid the meals being kept, and anything eaten in the last 4 weeks.
    const avoid = [
      ...currentMealPlan.selectedRecipes.map(r => r.id),
      ...recentlyPickedIds(pickHistory),
    ];
    const replacements = pickReplacements(recipes, rerolling.length, avoid, unrepresentedTerms);

    if (replacements.length < rerolling.length) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `I've run out of recipes I haven't suggested recently. Say "new" for a fresh set.`
      }]);
      return;
    }

    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    // Build the new meals one at a time so each replacement sees the kept
    // meals *and* the replacements already decided, and can vary against both.
    const newMealsByIndex = {};
    for (let slot = 0; slot < rerolling.length; slot++) {
      const index = rerolling[slot];
      const recipe = replacements[slot];

      setLoadingStatus(
        rerolling.length === 1
          ? '🧠 AI suggesting sides for new recipe...'
          : `🧠 AI suggesting sides (${slot + 1} of ${rerolling.length})...`
      );

      const contextMeals = [
        ...keptIndexes.map(i => currentMealPlan.meals[i]).filter(Boolean),
        ...Object.values(newMealsByIndex),
      ];
      const sides = await getAISidesForRecipe(recipe, contextMeals, signal, pickHistory);

      setLoadingStatus('🖼️ Fetching photo...');
      let imageUrl = null;
      try {
        const res = await fetch(`/api/food-image?q=${encodeURIComponent(recipe.name)}`);
        const data = await res.json();
        imageUrl = data.image || null;
      } catch (e) {}

      newMealsByIndex[index] = {
        mealNum: `MEAL ${index + 1}`,
        recipeId: recipe.id,
        sides,
        recipe,
        imageUrl,
      };
    }

    // Splice the new meals in. Any kept slot with no stored card (the AI
    // skipped it on the original run) gets a plain one rebuilt from the recipe.
    const fallbackMeal = (i) => ({
      mealNum: `MEAL ${i + 1}`,
      recipeId: currentMealPlan.selectedRecipes[i].id,
      sides: null,
      recipe: currentMealPlan.selectedRecipes[i],
    });
    const newMeals = Array.from({ length: n }, (_, i) =>
      newMealsByIndex[i] ?? currentMealPlan.meals[i] ?? fallbackMeal(i)
    );
    const newMealCards = Array.from({ length: n }, (_, i) =>
      newMealsByIndex[i]
        ? buildMealCard(newMealsByIndex[i], newMealsByIndex[i].imageUrl)
        : currentMealPlan.mealCards[i] ?? buildMealCard(fallbackMeal(i), null)
    );
    const newSelectedRecipes = Array.from({ length: n }, (_, i) =>
      newMealsByIndex[i]?.recipe ?? currentMealPlan.selectedRecipes[i]
    );

    const cleanedMessage = newMealCards
      .flatMap((card, i) => (i === 0 ? card : ['', ...card]))
      .join('\n');

    setMessages(prev => [...prev, { role: 'assistant', content: cleanedMessage }]);
    setCurrentMealPlan({ selectedRecipes: newSelectedRecipes, meals: newMeals, mealCards: newMealCards });
    setSeenRecipeIds(prev => [...prev, ...replacements.map(r => r.id)]);
    // The kept meals keep their locks; the rerolled ones come back unlocked.
    setLockedIndexes(prev => prev.filter(i => !rerolling.includes(i)));
    // Ticks for ingredients the new meals no longer need are dropped, so the
    // checklist only ever remembers things still on it.
    const stillNeeded = new Set(buildShoppingChecklist(newMeals).items.map(item => item.key));
    setCheckedItems(prev => prev.filter(key => stillNeeded.has(key)));
    setMessages(prev => [...prev, { role: 'assistant', content: nextStepsMessage }]);
  } catch (error) {
    if (error.name === 'AbortError') return;
    console.error('Reroll error:', error);
    setMessages(prev => [...prev, { role: 'assistant', content: `Sorry, error re-rolling: ${error.message}` }]);
  } finally {
    setLoading(false);
    setLoadingStatus('');
  }
};

const sendMessage = async (e) => {
  e.preventDefault();

const isEmpty = !input.trim();
// An empty Enter normally means "roll me a plan". While a calendar is
// waiting it means "give me the file" instead: rolling 3 new recipes there
// would throw away the plan the calendar was built from.
const userMessage = isEmpty ? (pendingCalendar ? 'download calendar' : 'go') : input.trim();
setInput('');
setMessages(prev => [...prev, { role: 'user', content: userMessage }]);

// A calendar is waiting: any way of asking for it hands over the file.
// This runs before every other command so a plan can never be re-rolled out
// from under a calendar the user is in the middle of downloading.
if (pendingCalendar && isCalendarDownloadRequest(userMessage)) {
  // Hand back the file built when the dates were given, rather than
  // rebuilding it here from a subset of the same data.
  downloadICSFile(pendingCalendar.icsContent);
  setPendingCalendar(null);

  setMessages(prev => [...prev, {
    role: 'assistant',
    content: 'Calendar created! The ICS file should download automatically. You can import it into any calendar app.'
  }]);
  return;
}

// Check if user is providing calendar dates
const isCalendarRequest = /\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+\d{1,2}/i.test(userMessage) && currentMealPlan;

if (isCalendarRequest) {
  // Handle calendar generation with JavaScript instead of AI
  generateCalendarFromMessage(userMessage);
  return;
}

// Detect "calendar me" / "make a calendar" without dates → prompt for dates
const isCalendarIntent = /\bcalendar\b/i.test(userMessage);
if (isCalendarIntent && currentMealPlan) {
  setMessages(prev => [...prev, {
    role: 'assistant',
    content: `Sure! What 3 dates should I put these meals on? (e.g. "Jan 10, 12, 14")`
  }]);
  return;
}

// "keep #2, redo the others", "swap 2", "redo 1 and 3" → re-roll only the
// meals the user did not keep. The locked meals are never regenerated, so
// they cannot drift or disappear.
const keepCommand = currentMealPlan ? parseKeepCommand(userMessage, currentMealPlan.selectedRecipes.length) : null;
if (keepCommand) {
  await rerollMeals(keepCommand.reroll);
  return;
}

// A time ceiling ("something quick", "under 30 minutes") can only be honoured
// by picking different recipes, so it is a fresh roll rather than a tweak of
// the three already on screen.
const asksForQuicker = !isEmpty && parseTimeLimit(userMessage) !== null;

// If a plan already exists and this isn't a "new"/"start over" request, treat as a tweak
const isNewRequest = isEmpty || /\b(new|start over|again|redo|re-do|fresh)\b/i.test(userMessage) || asksForQuicker || !currentMealPlan;
if (!isNewRequest && currentMealPlan) {
  // Send to AI with the SAME recipes, letting it know what the user wants tweaked
  setLoading(true);
  setLoadingStatus('🧠 Tweaking your meal plan...');
  try {
    await runMealPlan(currentMealPlan.selectedRecipes, userMessage.split(/[,/]|and\b/i).map(t => t.trim()).filter(t => t.length > 1));
  } catch (error) {
    if (error.name === 'AbortError') return;
    console.error('Tweak error:', error);
    setMessages(prev => [...prev, { role: 'assistant', content: `Sorry, error: ${error.message}` }]);
  } finally {
    setLoading(false);
    setLoadingStatus('');
  }
  return;
}

setLoading(true);

  try {
    // Fresh roll: parse user's spare ingredients (reuse original if this is a "new" request)
    const isNew = /\b(new|start over|again|redo|re-do|fresh)\b/i.test(userMessage);

    // "quick" or "under 30 minutes" is a time ceiling, not an ingredient, so
    // lift it out before the rest is read as a shopping list.
    const timeLimit = parseTimeLimit(isEmpty ? '' : userMessage);
    const termSource = timeLimit ? timeLimit.cleaned : (isEmpty ? '' : userMessage.trim());
    const parsedTerms = parseIngredientTerms(termSource);
    const userTerms = isNew ? originalUserTerms : parsedTerms;
    if (!isNew) setOriginalUserTerms(parsedTerms);

    // A time ceiling can only be honoured by recipes whose document actually
    // states a time. filterByTimeLimit returns null when nothing qualifies,
    // so we say so rather than quietly ignoring the request.
    let timeFiltered = recipes;
    if (timeLimit) {
      const fits = filterByTimeLimit(recipes, timeLimit.maxMinutes);
      if (!fits) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `I don't have any recipes with a stated time under ${timeLimit.maxMinutes} minutes. Plenty of recipes don't list a time at all, and I'd rather not guess. Try a longer limit, or just say "go".`
        }]);
        return;
      }
      timeFiltered = fits;
      setLoadingStatus(`⏱️ ${fits.length} recipes come in under ${timeLimit.maxMinutes} minutes...`);
    }

    // Exclude recipes seen this session and anything eaten in the last 4
    // weeks, so the randomizer doesn't serve the same dinners again.
    // Reset the exclusion list once it would consume more than half the pool.
    const avoidIds = [...seenRecipeIds, ...recentlyPickedIds(pickHistory)];
    const fresh = timeFiltered.filter(r => !avoidIds.includes(r.id));
    const pool = fresh.length >= 9 ? fresh : timeFiltered;
    if (fresh.length < 9) setSeenRecipeIds([]);

    // Ingredient searches match against the full (time-filtered) recipe list so
    // an explicit ingredient (e.g. "chuck roast") is always honored even if
    // previously seen. Random fills still draw from the unseen pool to avoid repeats.
    const matchPool = userTerms.length > 0 ? timeFiltered : pool;
    const { selectedRecipes, unmatchedTerms } = selectRecipes(matchPool, userTerms, pool);
    setSeenRecipeIds(prev => [...prev, ...selectedRecipes.map(r => r.id)]);
    // A whole new set: the old lock numbers would point at different meals.
    setLockedIndexes([]);
    setCheckedItems([]);

    setLoadingStatus(`🎲 Picked 3 random recipes...`);
    await runMealPlan(selectedRecipes, unmatchedTerms);
  } catch (error) {
    if (error.name === 'AbortError') return;
    console.error('API Error:', error);
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: `Sorry, I encountered an error: ${error.message}.${window.location.hostname === 'localhost' ? ' Make sure the backend is running on port 3002.' : ''}`
    }]);
  } finally {
    setLoading(false);
    setLoadingStatus('');
  }
};

  const runMealPlan = async (selectedRecipes, userTerms) => {
    // What was eaten recently, so the AI varies its sides away from it.
    const historyContext = formatPickHistoryForPrompt(pickHistory);

    const recipesContext = selectedRecipes.map((r, i) =>
      // Recipe IDs are Google Doc ids, and the parser matches meals by
      // position rather than by the echoed id, so they're left out of the
      // prompt instead of asking the model to copy a 44-character string.
      `MEAL ${i + 1}: ${r.name}
Ingredients: ${r.ingredients.join(', ')}
Cook time: ${formatDuration(r.cookTime)}
Total time: ${formatDuration(r.totalTime)}${r.servesWith ? `\nThe recipe says to serve it with: ${r.servesWith}` : ''}
URL: ${r.url}`
    ).join('\n\n');

    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setLoadingStatus('🧠 AI analyzing ingredients and suggesting sides...');
    const systemPrompt = `You are a meal planning assistant. I've already selected 3 random recipes for this week's dinners. Your job is to:

1. ANALYZE each recipe's ingredients
2. DETERMINE what's missing to make a complete meal
3. SUGGEST simple sides to add

🍽️ COMPLETE MEAL RULES - CRITICAL:
Every dinner MUST have ALL THREE components:
1. PROTEIN: meat (beef, pork, lamb, chicken, fish), tofu, tempeh, beans, lentils, chickpeas, eggs, cheese
2. VEGETABLES: leafy greens, broccoli, carrots, peppers, mushrooms, tomatoes, etc. (NOT just onions/garlic)
3. CARBS: rice, pasta, bread, potatoes, quinoa, couscous, grains

⚠️ ANALYZE EACH RECIPE'S INGREDIENTS CAREFULLY:
Check what it contains:
- Look at the ingredient list to identify what's actually in the recipe
- Determine what's MISSING (protein, vegetables, or carbs)
- ADD simple sides to complete the meal

📌 IF THE RECIPE SAYS WHAT TO SERVE IT WITH:
When a meal includes a line "The recipe says to serve it with: X", use X as
the carb or base. Do NOT substitute your own. If the recipe says egg noodles,
add egg noodles, not rice.

🥄 COMPONENT RECIPES - sauces, dressings, pestos, dips:
Some recipes are a component, not a centerpiece: a sauce, a dressing, a
vinaigrette, a pesto, a dip, a condiment. For these, do NOT treat the
component as the main dish and add sides to it. Instead, name the dish it
belongs to and build that meal around it.
- A dressing or vinaigrette → a salad it dresses, plus a protein.
  "Creamy Balsamic Vinaigrette" → Add: "mixed greens salad + grilled chicken"
- A pasta sauce → the pasta, plus whatever else the meal needs.
  "Marcella Hazan's Tomato Sauce" → Add: "spaghetti + green salad"
- A pesto or dip → what it goes on, plus a protein and vegetable.
  "Arugula Pesto" → Add: "pasta + white beans"
Judge this from the recipe NAME and its ingredients. A dish that merely has a
sauce in its name ("Seared Salmon with Porcini Cream Sauce", "Fish Tacos with
Best Fish Taco Sauce") is a full dinner, not a component.

PROTEIN VARIETY - IMPORTANT:
When adding protein sides, VARY the proteins across meals:
- Meal 1: chicken breast OR tofu OR salmon
- Meal 2: chickpeas OR black beans OR eggs
- Meal 3: tempeh OR pork OR firm tofu
DO NOT default to chicken breast for everything!

⚠️ VEGETABLES RULE - BE STRICT:
Onions, garlic, and peppers used as base aromatics do NOT count as a vegetable component.
A meal needs a real veggie: leafy greens, broccoli, carrots, green beans, tomatoes, a salad, roasted veggies, etc.
Tomatoes ARE a vegetable for meal planning purposes — if a recipe features tomatoes as a main ingredient, it counts.
If a recipe is a stew or sauce-based dish with only aromatics, it NEEDS a veggie side.

⚠️ PROTEIN RULE - BE PRACTICAL:
ANY dish where cheese or eggs are a main ingredient already has protein. This includes mac and cheese, lasagna, pizza, caprese salad, quiche, omelets, cheese-based soups, etc.
Do NOT add tofu, beans, or meat to these dishes. Just add what's actually missing (usually a veggie or carb).

EXAMPLES OF ANALYSIS:
- "Basic Chard" ingredients: chard, onion, oil, vinegar
  → Has: vegetables (chard)
  → Missing: PROTEIN + CARBS
  → Add: "baked tofu + quinoa"

- "Escarole and Beans" ingredients: escarole, beans, bacon, garlic, broth
  → Has: vegetables (escarole), protein (beans + bacon)
  → Missing: CARBS
  → Add: "rice" OR "crusty bread"

- "Pasta Primavera" ingredients: pasta, mixed vegetables, parmesan
  → Has: carbs (pasta), vegetables (mixed veg), protein (parmesan cheese)
  → Missing: NOTHING - complete meal!

- "Hungarian Goulash" ingredients: beef, onion, paprika, egg noodles, broth
  → Has: protein (beef), carbs (egg noodles)
  → Onion is just an aromatic, does NOT count as vegetable
  → Missing: VEGETABLES
  → Add: "steamed green beans" OR "side salad"

- "Mac and Cheese" ingredients: pasta, cheddar, gruyere, butter, milk
  → Has: carbs (pasta), protein (cheese is plenty)
  → Missing: VEGETABLES only
  → Add: "steamed broccoli" or "side salad"
  → Do NOT add chickpeas or beans — cheese is enough protein

- "Caprese Salad" ingredients: tomatoes, fresh mozzarella, basil, olive oil
  → Has: vegetables (tomatoes), protein (mozzarella IS protein)
  → Missing: CARBS only
  → Add: "crusty bread" or "quinoa"
  → Do NOT add tofu or meat — mozzarella is enough protein

Here are the 3 recipes I've selected:
${recipesContext}
${userTerms.length > 0 ? `\nThe user wants to use up or incorporate these ingredients: ${userTerms.join(', ')}.\nWhen suggesting sides to complete a meal, PRIORITIZE these ingredients over generic defaults.` : ''}
${historyContext ? `\n${historyContext}` : ''}

Your responsibilities:
1. ANALYZE each recipe's ingredients and ADD ONLY what's missing:
   - Missing PROTEIN? → Add: tofu, tempeh, beans, chickpeas, eggs, or meat (VARY the proteins!)
   - Missing VEGETABLES? → Add: roasted broccoli, steamed green beans, side salad, sautéed spinach
   - Missing CARBS? → Add: rice, quinoa, couscous, roasted potatoes, crusty bread
     ⚠️ UNLESS that meal has a "The recipe says to serve it with: X" line.
     Then the carb you add MUST be X. Recipe says egg noodles → add egg
     noodles, never rice or potatoes instead.

   ⚠️ IMPORTANT: Only add sides for MISSING components:
   - If recipe has vegetables (spinach, chard, salad greens, etc.) → DO NOT add more vegetables
   - If recipe has protein (chicken, tofu, beans, cheese, etc.) → DO NOT add more protein
   - If recipe has carbs (pasta, rice, orzo, bread, etc.) → DO NOT add more carbs

2. 🚨 CRITICAL - SHOPPING LIST MUST BE COMPLETE:
   When you add sides, you MUST include those ingredients in the shopping list!

   CORRECT examples:
   - Add "roasted broccoli + quinoa" → Shopping list includes: broccoli, quinoa
   - Add "side salad" → Shopping list includes: salad greens, tomatoes, cucumber
   - Add "baked tofu" → Shopping list includes: firm tofu
   - Add "rice" → Shopping list includes: rice

3. 🚨 SHOPPING LIST - FILTER OUT STOCK ITEMS:
   The "Needed ingredients" list is for SHOPPING, not cooking. Only list what someone needs to BUY.

   ❌ NEVER INCLUDE these stock pantry items (people already have these):
   - ALL seasonings/spices: salt, pepper, Italian seasoning, dried herbs (thyme, basil, oregano), red pepper flakes, bay leaf
   - ALL oils/fats: olive oil, vegetable oil, butter, cooking spray
   - ALL aromatics: garlic (fresh or dried/minced), onion (fresh or dried/minced), bell pepper
   - ALL condiments: vinegar (any type), lemon juice, lime juice, soy sauce, Worcestershire sauce, hot sauce
   - ALL baking basics: flour, sugar, brown sugar, baking soda, baking powder
   - ALL dairy staples: eggs, milk
   - Ground pepper (black, white, any color)

   ✅ DO INCLUDE items people need to buy:
   - Fresh proteins: chicken breast, salmon, tofu, tempeh, ground beef
   - Fresh vegetables: broccoli, salad greens, bell peppers, carrots, chard, escarole
   - Grains/carbs: rice, quinoa, pasta, couscous, bread
   - Specialty items: chickpeas, beans, cannellini beans, nuts, cheese (parmesan, feta)
   - Fresh ingredients: lemon, lime, fresh parsley, fresh ginger

   EXAMPLE - Escarole and Beans recipe:
   Recipe ingredients: bacon, olive oil, escarole, garlic, chicken broth, cannellini beans, red pepper flakes, parmesan
   ✅ Shopping list should be: bacon, escarole, chicken broth, cannellini beans, parmesan
   ❌ DO NOT include: olive oil, garlic, red pepper flakes (these are pantry staples)

4. ⚠️ BEFORE WRITING EACH MEAL - FINAL REVIEW:
   Go through your shopping list and DELETE any of these items:
   - salt, pepper, any spices, Italian seasoning, herbs, bay leaf
   - olive oil, vegetable oil, butter
   - garlic, onion, bell pepper (unless it's a main ingredient like stuffed peppers)
   - lemon juice, lime juice, vinegar
   - dried minced anything (garlic, onion)

🚨 REQUIRED FORMAT - Follow this EXACTLY:
Your ENTIRE reply must start with "MEAL 1". Do NOT write any analysis, reasoning,
headings, or commentary before it - output only the meal blocks below, then the
calendar question. Keep it terse; there is a hard output limit.
MEAL [number]
- Cooking time: X min
- Add to complete meal: [ONLY ingredient names, e.g. "baked tofu + brown rice". No explanations. Or "Nothing needed" if already complete.]
- Shopping list:
  • [ingredient 1 - NO salt/pepper/oil/garlic/onion/spices]
  • [ingredient 2 - NO salt/pepper/oil/garlic/onion/spices]
  • [ingredient 3 - NO salt/pepper/oil/garlic/onion/spices]

FORMATTING RULES:
- NO preamble, NO analysis section, NO summary - begin at "MEAL 1"
- Add to complete meal = ONLY ingredient names (e.g. "baked tofu + brown rice"), no explanations, or "Nothing needed"
- FINAL CHECK: Remove salt, pepper, oil, butter, garlic, onion, vinegar, lemon/lime juice, spices, dried seasonings
- Use simple bullet points (•) only
- List ingredients as a flat list, not grouped by sections

EXAMPLE:
MEAL 1
- Cooking time: 25 min
- Add to complete meal: rice
- Shopping list:
  • bacon
  • escarole
  • chicken broth
  • cannellini beans
  • parmesan cheese
  • rice

Focus on making every meal satisfying, complete, and VARIED in protein sources!

CALENDAR WORKFLOW - DO NOT CREATE CALENDAR AUTOMATICALLY:
⚠️ IMPORTANT: Only mention the calendar AFTER presenting the meal plan.

Step 1: Present the 3 meal plan
Step 2: STOP and ask: "Would you like me to create a calendar for these meals? If yes, please provide 3 specific dates (e.g., January 15, January 17, January 20)"
Step 3: WAIT for the user to provide dates - DO NOT assume dates or create a preview automatically
Step 4: Only when user provides dates, show the calendar preview
Step 5: Wait for user to say "create calendar" before generating ICS format`;

    // Use EventSource for streaming responses
    const response = await fetch('/api/chat', {
      signal,
      method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: [{ role: 'user', content: 'Please analyze these recipes and create the meal plan.' }],
          systemPrompt: systemPrompt
        })
      });

    // Handle streaming response
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let assistantMessage = '';
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
          const data = JSON.parse(line.substring(6));

          if (data.type === 'progress') {
            // Update status with progress info
            setLoadingStatus(`⏳ AI generating meal plan... (${data.elapsed}s elapsed, ${data.tokens} tokens)`);
          } else if (data.done) {
            // Final response received
            assistantMessage = data.content[0].text;
          } else if (data.error) {
            throw new Error(data.error);
          }
          } catch (parseError) {
            console.warn('Failed to parse streaming chunk:', line);
          }
        }
      }
    }

setLoadingStatus('🔗 Adding recipe links and finalizing meal plan...');

      if (!assistantMessage) {
        throw new Error('No response received from AI');
      }

// Rebuild meals with correct data from recipes.json

// Parse meals and extract structure from AI response
const lines = assistantMessage.split('\n');
console.log('Total lines in AI response:', lines.length);
console.log('First 10 lines:', lines.slice(0, 10));
const meals = [];
let currentMeal = null;
const otherLines = [];

// Match meals with selectedRecipes by order
for (let i = 0; i < lines.length; i++) {
  const line = lines[i];

  if (line.match(/^\s*MEAL\s*(\d+)/)) {
    const mealNum = parseInt(line.match(/^\s*MEAL\s*(\d+)/)[1]);
    console.log('Found meal header at line', i, ':', line);
    if (currentMeal) meals.push(currentMeal);

    if (mealNum < 1 || mealNum > selectedRecipes.length) {
      console.warn(`Unexpected meal number ${mealNum} (have ${selectedRecipes.length} recipes), skipping`);
      currentMeal = null;
      continue;
    }

    // Use the recipe we already selected (by index)
    const recipe = selectedRecipes[mealNum - 1];
    console.log(`Meal ${mealNum} selectedRecipes array:`, selectedRecipes);
    console.log(`Looking for recipe at index ${mealNum - 1}:`, recipe);
    currentMeal = {
      mealNum: line,
      recipeId: recipe?.id,
      sides: null,
      recipe: recipe
    };
    console.log(`Meal ${mealNum} -> ${recipe?.name} (${recipe?.id})`);
  } else if (currentMeal && line.includes('Recipe ID:')) {
    // Skip - we already have the recipe from selectedRecipes
  } else if (currentMeal && line.includes('Add to complete meal:')) {
    currentMeal.sides = line.substring(line.indexOf(':') + 1).trim();
  } else if (currentMeal && line.includes('Cooking time:')) {
    // Skip - we'll use recipe data
  } else if (currentMeal && (line.includes('Needed ingredients') || line.includes('Shopping list'))) {
    // Skip the shopping list header - we'll rebuild from recipe data
  } else if (currentMeal && line.trim().startsWith('•')) {
    // Skip ingredient bullets - we'll rebuild from recipe data
  } else if (!currentMeal) {
    // Lines before first meal (like intro text)
    otherLines.push(line);
  }
}

// Don't forget the last meal
if (currentMeal) meals.push(currentMeal);

// If the AI skipped any recipe, fill in a default card with no sides.
const mealsIndexed = {};
meals.forEach(m => {
  const idx = selectedRecipes.findIndex(r => r.id === m.recipeId);
  if (idx >= 0) mealsIndexed[idx] = m;
});
const allMeals = selectedRecipes.map((recipe, i) =>
  mealsIndexed[i] ?? { mealNum: `MEAL ${i + 1}`, recipeId: recipe.id, sides: null, recipe }
);

// Now rebuild each meal with complete data from recipes.json
// Fetch Unsplash images for each meal in parallel
const foodImages = await Promise.all(allMeals.map(async (mealData) => {
  try {
    if (!mealData.recipe?.name) return null;
    const res = await fetch(`/api/food-image?q=${encodeURIComponent(mealData.recipe.name)}`);
    const data = await res.json();
    return data.image || null;
  } catch (e) {
    return null;
  }
}));

// Build per-meal card arrays, then join into one message
const mealCards = allMeals.map((mealData, index) => buildMealCard(mealData, foodImages[index]));
const finalMessage = [
  ...otherLines,
  ...mealCards.flatMap((card, i) => i === 0 ? card : ['', ...card])
];

const cleanedMessage = finalMessage.join('\n');

    const assistantMsg = { role: 'assistant', content: cleanedMessage };
    setMessages(prev => [...prev, assistantMsg]);
    setLastAssistantMessage(assistantMsg);
    // Store imageUrl in each meal so swap can reuse it without re-fetching
    const mealsWithImages = allMeals.map((m, i) => ({ ...m, imageUrl: foodImages[i] }));
    setCurrentMealPlan({ selectedRecipes, meals: mealsWithImages, mealCards });

    // Prompt user with next steps
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: nextStepsMessage
    }]);
  };


  // ─── Plan panel ───────────────────────────────────────────────────────────

  const planMeals = currentMealPlan?.meals ?? [];
  const checklist = buildShoppingChecklist(planMeals);
  // A re-roll can leave ticks behind for ingredients the new meals don't
  // need. Counting and copying both work off the items actually on the list,
  // so a stale tick can never inflate the count or the clipboard.
  const checklistKeys = new Set(checklist.items.map(item => item.key));
  const activeChecked = checkedItems.filter(key => checklistKeys.has(key));
  const checkedCount = activeChecked.length;
  const picksSaved = currentMealPlan ? alreadySaved(currentMealPlan.selectedRecipes) : false;

  const toggleLock = (index) => {
    setLockedIndexes(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  // What a meal row says about itself, beyond its name and time.
  const pickingNotes = (meal) => {
    const notes = [];

    const main = summarizeIngredients(meal?.recipe?.ingredients, 3);
    if (main.length > 0) notes.push(main.join(', '));

    const servedWith = meal?.recipe?.servesWith || parseSidesTitle(meal?.sides);
    if (servedWith) notes.push(`with ${servedWith}`);

    return notes;
  };

  const toggleChecked = (key) => {
    setCheckedItems(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const rerollUnlocked = () => {
    const unlocked = planMeals.map((_, i) => i).filter(i => !lockedIndexes.includes(i));
    if (unlocked.length === 0 || unlocked.length === planMeals.length) return;
    rerollMeals(unlocked);
  };


  return (
    <div className="app-container">
      <div className="app-header">
        <div className="header-content">
          <div className="header-title">
            <img src="/mamas_menu_maker_icon.png" alt="Mama's Menu Maker" style={{width: 60, height: 60}} />
            <div>
              <h1>Mama's Menu Maker</h1>
              <p>AI dinner planning made easy</p>
            </div>
          </div>
        </div>
      </div>

      <div className="messages-container">
        <div className="messages-wrapper">
          {messages.map((msg, i) => (
            <div key={i} className={`message message-${msg.role}`}>
              <div className="message-bubble">
{msg.role === 'assistant' ? (
  <div dangerouslySetInnerHTML={{
    __html: msg.content
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>') // Convert **text** to <strong>
      .replace(/Recipe ID: ([^\n\s]+)/g, 'Recipe ID: <code>$1</code>') // Style recipe IDs
  }} />
) : (
  msg.content
)}
              </div>
            </div>
          ))}
{loading && (
  <div className="message message-assistant">
    <div className="message-bubble">
      <div className="loading-container">
        <video
          className="loading-animation-video"
          autoPlay
          loop
          muted
          playsInline
        >
          <source src="/xiebob_a_warm_friendly_icon_for_an_app_called_Mamas_Menu_Make_527582d6-7d36-4405-9136-205bd0eacb3f_1.mp4" type="video/mp4" />
        </video>
        {loadingStatus && (
          <div className="loading-status">
            {loadingStatus}
          </div>
        )}
      </div>
    </div>
  </div>
)}
          {currentMealPlan && !loading && (
            <div className="plan-panel">
              <div className="plan-panel-section">
                <h3 className="plan-panel-heading">This week's meals</h3>
                {planMeals.map((meal, i) => {
                  const locked = lockedIndexes.includes(i);
                  const notes = pickingNotes(meal);
                  const lastEaten = meal?.recipe?.id
                    ? lastEatenLabel(meal.recipe.id, pickHistory)
                    : null;
                  return (
                    <label key={i} className={`plan-meal-row${locked ? ' is-locked' : ''}`}>
                      <input
                        type="checkbox"
                        checked={locked}
                        onChange={() => toggleLock(i)}
                      />
                      {meal?.imageUrl ? (
                        <img
                          className="plan-meal-photo"
                          src={meal.imageUrl}
                          alt=""
                          loading="lazy"
                        />
                      ) : (
                        <span className="plan-meal-photo plan-meal-photo-empty" aria-hidden="true">🍽️</span>
                      )}
                      <span className="plan-meal-text">
                        <span className="plan-meal-name">
                          {i + 1}. {meal?.recipe?.name?.trim() || `Meal ${i + 1}`}
                          {locked && <span className="plan-meal-lock" aria-hidden="true"> 🔒</span>}
                        </span>
                        {notes.length > 0 && (
                          <span className="plan-meal-notes">{notes.join(' · ')}</span>
                        )}
                        <span className="plan-meal-meta">
                          {formatTotalTime(meal?.recipe?.totalTime)}
                          <span className={lastEaten ? 'plan-meal-seen' : 'plan-meal-new'}>
                            {lastEaten || 'new to you'}
                          </span>
                        </span>
                      </span>
                    </label>
                  );
                })}

                <div className="plan-panel-actions">
                  <button
                    type="button"
                    className="plan-button plan-button-primary"
                    onClick={rerollUnlocked}
                    disabled={lockedIndexes.length === 0 || lockedIndexes.length === planMeals.length}
                  >
                    {lockedIndexes.length === 0
                      ? 'Lock a meal to re-roll the rest'
                      : lockedIndexes.length === planMeals.length
                        ? 'All meals locked'
                        : `Re-roll the other ${planMeals.length - lockedIndexes.length}`}
                  </button>
                  <button
                    type="button"
                    className="plan-button"
                    onClick={() => recordPicks(currentMealPlan.selectedRecipes)}
                    disabled={picksSaved}
                  >
                    {picksSaved ? '✓ Saved to history' : 'Accept these 3'}
                  </button>
                </div>
              </div>

              {checklist.items.length > 0 && (
                <details className="plan-panel-section checklist-details">
                  <summary className="plan-panel-heading">
                    Shopping checklist
                    <span className="plan-panel-hint">
                      {checkedCount > 0
                        ? `${checkedCount} to buy`
                        : 'tick only what you need to buy'}
                    </span>
                  </summary>

                  {checklist.groups.map(group => (
                    <div key={group.mealNum} className="checklist-group">
                      <div className="checklist-group-name">{group.mealNum}. {group.mealName}</div>
                      {group.items.map(item => (
                        <label key={item.key} className="checklist-item">
                          <input
                            type="checkbox"
                            checked={checkedItems.includes(item.key)}
                            onChange={() => toggleChecked(item.key)}
                          />
                          <span>{item.label}</span>
                          {item.meals.length > 1 && (
                            <span className="checklist-shared">
                              also meal {item.meals.slice(1).join(', ')}
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                  ))}

                  {checkedCount > 0 && (
                    <div className="checklist-output">
                      <div className="checklist-output-heading">
                        Needed ingredients
                        <button
                          type="button"
                          className="checklist-clear"
                          onClick={() => setCheckedItems([])}
                        >
                          Clear
                        </button>
                      </div>
                      {/* Plain selectable text: select it and paste it wherever
                          the list lives, without a clipboard round trip. */}
                      <pre className="checklist-output-list">{checklistToText(checklist, activeChecked)}</pre>
                    </div>
                  )}
                </details>
              )}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="input-container">
        <div className="input-wrapper">
          <form onSubmit={sendMessage} className="input-form">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message..."
              disabled={loading}
              className="message-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="send-button"
            >
              Go
            </button>
          </form>
          
{pendingCalendar && (
  <button
    onClick={() => {
      downloadICSFile(pendingCalendar.icsContent);
      setPendingCalendar(null); // Clear after download
    }}
    className="download-button"
    style={{ marginTop: '8px' }}
  >
    📅 Download ICS File
  </button>
)}        </div>
      </div>
    </div>
  );
};

export default MealPlannerChat;